import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSubmission } from "../services/exams";
import toast, { Toaster } from "react-hot-toast";
import { ArrowLeft } from "lucide-react";
import confetti from "canvas-confetti";
import { Star, Award } from "lucide-react";

export default function ExamResult() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSubmission(submissionId)
      .then((res) => {
        setData(res.data);
        setLoading(false);
        // 🎉 GAMIFICATION: Fire confetti on success
        if (res.data.submission?.stars_earned > 0) {
          confetti({
            particleCount: 150,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#FFD700', '#FFA500', '#FF4500']
          });
        }
      })
      .catch((err) => {
        toast.error("Failed to load result");
        setLoading(false);
      });
  }, [submissionId]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-lg font-bold text-slate-500 animate-pulse">Loading result...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <Toaster />
        <div className="text-center">
          <div className="text-xl font-bold text-red-600 mb-4">Result not found</div>
          <button onClick={() => navigate("/dashboard")} className="text-blue-600 hover:underline">Go to Dashboard</button>
        </div>
      </div>
    );
  }

  const { submission, exam, questions } = data;
  const answers = submission.answers || {};

  // Group calculations
  const groupStats = {};

  questions.forEach(q => {
    const groupName = q.group_name || "General Knowledge";
    if (!groupStats[groupName]) {
      groupStats[groupName] = {
        name: groupName,
        total: 0,
        attempted: 0,
        correct: 0,
        incorrect: 0,
        score: 0,
        maxScore: 0
      };
    }

    const group = groupStats[groupName];
    group.total += 1;
    group.maxScore += q.marks || 1;

    const studentAnswer = answers[q.id];
    if (studentAnswer) {
      group.attempted += 1;
      if (studentAnswer.toUpperCase() === q.correct_option?.toUpperCase()) {
        group.correct += 1;
        group.score += q.marks || 1;
      } else {
        group.incorrect += 1;
        group.score -= q.negative_marks || 0;
      }
    }
  });

  const groupsArray = Object.values(groupStats);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <Toaster />
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Rewards Celebration for Kids */}
        {submission.stars_earned > 0 && (
          <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-6 shadow-lg flex items-center justify-between text-white overflow-hidden relative">
            <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
            <div className="relative z-10 flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur p-4 rounded-full shadow-inner animate-bounce">
                <Award size={48} className="text-yellow-200" />
              </div>
              <div>
                <h2 className="text-2xl font-black">Amazing Job! 🚀</h2>
                <p className="font-medium opacity-90">You earned rewards for this challenge.</p>
              </div>
            </div>
            <div className="relative z-10 flex items-center gap-2 bg-white/90 px-6 py-3 rounded-2xl text-orange-600 font-black text-3xl shadow-xl animate-pulse">
              <Star fill="currentColor" size={28} className="text-amber-500" />
              +{submission.stars_earned}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4 transition-colors">
              <ArrowLeft size={16} /> Back
            </button>
            <h1 className="text-2xl font-black text-slate-900">{exam.title}</h1>
            <p className="text-slate-500 mt-1">Submitted on {new Date(submission.submitted_at).toLocaleString()}</p>
          </div>
          <div className={`px-6 py-4 rounded-xl border ${submission.passed ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"} text-center min-w-[200px]`}>
            <div className="text-sm font-bold uppercase tracking-wider mb-1 opacity-70">Overall Score</div>
            <div className={`text-4xl font-black ${submission.passed ? "text-emerald-600" : "text-red-600"}`}>
              {submission.percentage}%
            </div>
            <div className="text-sm font-bold mt-2 opacity-80">
              {submission.score} / {groupsArray.reduce((acc, g) => acc + g.maxScore, 0)} Marks
            </div>
          </div>
        </div>

        {/* Group-wise Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <h2 className="text-lg font-black text-slate-900">Group-wise Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-4 text-sm font-bold text-slate-600">Section / Group</th>
                  <th className="px-6 py-4 text-sm font-bold text-slate-600 text-center">Total Questions</th>
                  <th className="px-6 py-4 text-sm font-bold text-slate-600 text-center">Attempted</th>
                  <th className="px-6 py-4 text-sm font-bold text-emerald-600 text-center">Correct</th>
                  <th className="px-6 py-4 text-sm font-bold text-red-600 text-center">Incorrect</th>
                  <th className="px-6 py-4 text-sm font-bold text-blue-600 text-right">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groupsArray.map((group) => (
                  <tr key={group.name} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 font-bold text-slate-900">{group.name}</td>
                    <td className="px-6 py-4 text-center font-medium text-slate-600">{group.total}</td>
                    <td className="px-6 py-4 text-center font-medium text-slate-600">{group.attempted}</td>
                    <td className="px-6 py-4 text-center font-medium text-emerald-600">{group.correct}</td>
                    <td className="px-6 py-4 text-center font-medium text-red-600">{group.incorrect}</td>
                    <td className="px-6 py-4 text-right font-black text-blue-600">{group.score.toFixed(2)} / {group.maxScore}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr>
                  <td className="px-6 py-4 font-black text-slate-900">Total</td>
                  <td className="px-6 py-4 text-center font-black text-slate-900">{groupsArray.reduce((acc, g) => acc + g.total, 0)}</td>
                  <td className="px-6 py-4 text-center font-black text-slate-900">{groupsArray.reduce((acc, g) => acc + g.attempted, 0)}</td>
                  <td className="px-6 py-4 text-center font-black text-emerald-600">{groupsArray.reduce((acc, g) => acc + g.correct, 0)}</td>
                  <td className="px-6 py-4 text-center font-black text-red-600">{groupsArray.reduce((acc, g) => acc + g.incorrect, 0)}</td>
                  <td className="px-6 py-4 text-right font-black text-blue-600">
                    {groupsArray.reduce((acc, g) => acc + g.score, 0).toFixed(2)} / {groupsArray.reduce((acc, g) => acc + g.maxScore, 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
